import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO = 'https://customer-assets.emergentagent.com/job_vanta-elite-2/artifacts/v1pxetmi_8e228e61-2591-4f49-9930-974e8df7a274.png';

export default function Navbar() {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-black/60 backdrop-blur-3xl border-b border-white/8' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <div className="h-9 overflow-hidden rounded-lg shadow-[0_0_20px_rgba(255,255,255,0.1)] hover:shadow-[0_0_30px_rgba(255,255,255,0.2)] transition-all duration-300">
            <img src={LOGO} alt="Vanta Tweaks" className="h-full w-auto" />
          </div>
        </Link>

        {/* Center Nav */}
        <div className="hidden md:flex items-center gap-8">
          {[
            { label: t.nav.features, id: 'features' },
            { label: t.nav.shop, id: 'shop' },
            { label: t.nav.reviews, id: 'reviews' },
          ].map(item => (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => scrollTo(item.id)}
              className="relative text-zinc-400 hover:text-white text-sm font-inter transition-colors duration-200 group"
            >
              {item.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-white group-hover:w-full transition-all duration-300" />
            </button>
          ))}
        </div>

        {/* Right */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-full p-1 backdrop-blur-xl">
            <button
              data-testid="lang-en"
              onClick={() => setLang('en')}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-all duration-200 ${
                lang === 'en' ? 'bg-white text-black shadow-[0_0_10px_rgba(255,255,255,0.3)]' : 'text-zinc-400 hover:text-white'
              }`}
            >EN</button>
            <button
              data-testid="lang-de"
              onClick={() => setLang('de')}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-all duration-200 ${
                lang === 'de' ? 'bg-white text-black shadow-[0_0_10px_rgba(255,255,255,0.3)]' : 'text-zinc-400 hover:text-white'
              }`}
            >DE</button>
          </div>
          <button
            data-testid="nav-admin"
            onClick={() => navigate('/admin')}
            className="hidden md:block text-zinc-600 hover:text-zinc-400 text-xs font-inter transition-colors"
          >{t.nav.admin}</button>
        </div>
      </div>
    </motion.nav>
  );
}
